import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { Space } from '../../../ds';
import { StyledSelectOutline, StyledButton, HiddenDivInput } from './styled';
import { ReactComponent as IconArrowDown } from './icon-arrow-down.svg';
import {
  RiArrowDownSFill,
  RiArrowLeftSFill,
  RiArrowRightSFill,
  RiArrowUpSFill,
} from '@remixicon/react';
import { format } from 'date-fns';
import { es } from 'react-day-picker/locale';
import { DayPicker, TZDate } from 'react-day-picker';
import './calendar.scss';
import Timer from '../../../Utils/Timer';

const FORMAT_DATE = 'd/MM/yy';
const timeZone = 'America/Mexico_City';
// Gneral Focus Hook
const UseFocus = () => {
  const htmlElRef = useRef(null);
  const setFocus = () => {
    htmlElRef.current && htmlElRef.current.focus();
  };

  return [htmlElRef, setFocus];
};

const PlaceHolderLabel = ({ value, defaultValue }) => {
  if (!value) {
    return defaultValue;
  }

  return (
    <>
      <div>
        <span className='k-ds-heading-02'>Fecha:</span>
        <span className='k-ds-body-02'>{` ${format(value, FORMAT_DATE, {
          timeZone,
        })}`}</span>
      </div>
    </>
  );
};

PlaceHolderLabel.propTypes = {
  value: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
};

const Calendar = ({
  className,
  name,
  label,
  placeholder,
  onBlur,
  disabled,
  formatDate = '',
  value,
  onClick = () => null,
  ...moreProps
}) => {
  const { required } = moreProps;
  const props = { ...moreProps };

  const [isActive, setIsActive] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [ignoreBlur, setIgnoreBlur] = useState(false);
  const [inputHiddenRef, setInputHiddenFocus] = UseFocus();

  const [selected, setSelected] = useState(false);
  const [date, setDate] = useState(
    value ? new TZDate(Timer().toISOString(value)) : new TZDate()
  );

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
  }, []);

  const onSelectClick = () => {
    if (disabled) return;

    setIsActive(!isActive);
    setInputHiddenFocus();
  };

  const onOptionClick = () => {
    if (onClick) {
      onClick(
        format(date, formatDate, {
          timeZone,
        })
      );
    }
    setSelected(true);
    setIsActive(false);
    setIgnoreBlur(false);
  };

  const handleBlur = () => {
    if (ignoreBlur) return;
  };

  useEffect(() => {
    if (!isActive && !isFirstRender) {
      if (onBlur) onBlur();
    }
  }, [isActive]);

  const showClass = isActive && !disabled ? 'show' : '';

  return (
    <Space size='x-large' direction='vertical' className={className} {...props}>
      {label && (
        <Space
          size='small'
          justify='space-between'
          className='k-ds-width-full'
          direction='horizontal'
        >
          <label
            className='k-ds-body-01 k-ds-text-color-primary'
            htmlFor={name}
          >
            {label}
            {required ? (
              <span className='k-ds-text-color-error'>{' *'}</span>
            ) : null}
          </label>
        </Space>
      )}
      <StyledSelectOutline
        className='k-ds-width-full k-ds-body-01'
        disabled={disabled}
        showPlaceholder={date === ''}
        active={isActive}
      >
        <HiddenDivInput ref={inputHiddenRef} type='radio' onBlur={handleBlur} />
        <Space
          size='medium'
          justify='space-between'
          className={`k-ds-width-full selector ${selected ? 'selected' : ''}`}
          onClick={onSelectClick}
          direction='horizontal'
          align='center'
          aria-hidden
        >
          <div className='control'>
            <PlaceHolderLabel value={date} defaultValue={placeholder && ''} />
          </div>
          <IconArrowDown className={`arrow-icon ${showClass}`} />
        </Space>
        <div className={`options-group ${showClass}`}>
          <Space>
            <DayPicker
              mode='single'
              locale={es}
              timeZone={timeZone}
              selected={date}
              onSelect={setDate}
              components={{
                // eslint-disable-next-line react/prop-types
                Chevron({ orientation, disabled, className }) {
                  const Component =
                    orientation === 'left'
                      ? RiArrowLeftSFill
                      : orientation === 'right'
                        ? RiArrowRightSFill
                        : orientation === 'up'
                          ? RiArrowUpSFill
                          : RiArrowDownSFill;

                  return (
                    <Component className={className} aria-disabled={disabled} />
                  );
                },
                CaptionLabel(props) {
                  // eslint-disable-next-line react/prop-types
                  const label = String(props.children);
                  return (
                    <span {...props}>
                      {`${label.charAt(0).toUpperCase()}${label.slice(1)}`}
                    </span>
                  );
                },
                Weekday(props) {
                  // eslint-disable-next-line react/prop-types
                  const label = String(props.children);
                  return (
                    <th {...props}>
                      {`${label.charAt(0).toUpperCase()}${label.slice(1)}`}
                    </th>
                  );
                },
              }}
            />
          </Space>
          <StyledButton
            className='k-ds-m-top-sp-04'
            type='primary-alternate'
            horizontalPadding='1.125rem'
            verticalPadding='1rem'
            size='small'
            onMouseDown={() => setIgnoreBlur(true)}
            onClick={onOptionClick}
          >
            Aplicar
          </StyledButton>
        </div>
      </StyledSelectOutline>
    </Space>
  );
};

Calendar.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  formatDate: PropTypes.string,
  value: PropTypes.string,
};

export default Calendar;

//
